import * as Popover from '@radix-ui/react-popover';
import { Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/domains/identity/AuthProvider';
import { cn } from '@/shared/lib/cn';
import { formatTime } from '@/shared/lib/format';
import type { Locale } from '@/shared/i18n';
import { routes } from '../routes';

/**
 * The one place credit is displayed in the shell. One number, one source (`/auth/me.credits`).
 */
export function CreditChip() {
  const { t, i18n } = useTranslation();
  const { credits } = useAuth();

  if (!credits) return null;

  const unlimited = credits.unlimited;
  const remaining = credits.remaining ?? 0;
  const limit = credits.limit ?? 0;
  const ratio = limit > 0 ? remaining / limit : 1;
  // Low balance is signalled by colour AND the number itself, which is always visible.
  const low = !unlimited && ratio <= 0.15;

  return (
    <Popover.Root>
      <Popover.Trigger
        aria-label={t('a11y.creditBalance')}
        className={cn(
          'flex h-8 cursor-pointer items-center gap-1.5 rounded-full border px-2.5 text-sm font-medium tabular-nums',
          'transition-colors duration-150',
          low
            ? 'border-warning/40 bg-warning-subtle text-warning hover:bg-warning-subtle/80'
            : 'border-border bg-surface-raised text-fg-muted hover:bg-surface-hover hover:text-fg',
        )}
      >
        <Zap className="h-3.5 w-3.5" />
        {unlimited ? '∞' : `${remaining}/${limit}`}
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          className="z-50 w-72 animate-slide-up rounded-lg border border-border bg-surface-raised p-4 shadow-lg"
        >
          <p className="text-sm font-semibold">{t('credits.title')}</p>

          {unlimited ? (
            <p className="mt-2 text-base text-fg-muted">{t('common.unlimited')}</p>
          ) : (
            <>
              <div className="mt-3 flex items-baseline justify-between">
                <span className="text-xl font-semibold tabular-nums">{remaining}</span>
                <span className="text-sm text-fg-subtle">/ {limit}</span>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-hover">
                <div
                  className={cn('h-full rounded-full transition-[width] duration-300', low ? 'bg-warning' : 'bg-accent')}
                  style={{ width: `${Math.round(ratio * 100)}%` }}
                />
              </div>
              <dl className="mt-3 space-y-1 text-sm">
                <div className="flex justify-between">
                  <dt className="text-fg-muted">{t('credits.usedToday')}</dt>
                  <dd className="tabular-nums">{credits.used_today}</dd>
                </div>
                {credits.resets_at && (
                  <div className="flex justify-between">
                    <dt className="text-fg-muted">{t('credits.resetsAt', { time: '' }).replace('{{time}}', '').trim()}</dt>
                    <dd className="tabular-nums">{formatTime(credits.resets_at, i18n.language as Locale)}</dd>
                  </div>
                )}
              </dl>
              <p className="mt-2 text-xs text-fg-subtle">{t('credits.resetsDaily')}</p>
            </>
          )}

          <Popover.Close asChild>
            <Link
              to={routes.accountCredits}
              className="mt-3 block cursor-pointer text-sm font-medium text-accent hover:underline"
            >
              {t('credits.viewLedger')} →
            </Link>
          </Popover.Close>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
